import { expectAtIndex } from "@aprao/common/src/array/expectAtIndex.js";
import {
  type BigFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { zero as brentZero } from "brent-zero-generator";
import { action, makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";
import { UnreachableCaseError } from "ts-essentials";

import type { ShapeAppraisalDocument } from "../shapes/ShapeAppraisal.js";
import type { ShapeSalesTaxConfiguration } from "../shapes/ShapeProject.js";
import type { AppraisalDocumentFacade } from "./AppraisalDocumentFacade.js";
import {
  createAppraisalFacades,
  overridablePriceManagerFactory,
} from "./createAppraisalFacades.js";
import type { FlowFacade } from "./FlowFacade.js";
import type { SalesTaxConfigurationFacade } from "./SalesTaxConfigurationFacade.js";
import type { SitePurchaseFacade } from "./SitePurchaseFacade.js";

export interface SitePriceManager {
  getPriceBySite: (site: SitePurchaseFacade) => BigFraction | null;
}

/**
 * Retrieves the price for a given site purchase.
 *
 * It computes the residual price guess if calculation m ode is set to
 * "residual", or returns the defined amount if manual.
 */
export class SitePriceManagerImpl implements SitePriceManager {
  _appraisalDocument;

  _salesTaxConfiguration;

  constructor(
    appraisalDocument: AppraisalDocumentFacade,
    salesTaxConfiguration: SalesTaxConfigurationFacade | null,
  ) {
    makeAutoObservable(this);

    this._appraisalDocument = appraisalDocument;
    this._salesTaxConfiguration = salesTaxConfiguration;
  }

  getPriceBySite = computedFn(
    // A crucial optimization since it is an expensive function, and we call it
    // for every flow "frame".
    (site: SitePurchaseFacade) => {
      if (site.pricePurchase.calculationType === "manual") {
        return site.pricePurchase.definedAmount;
      } else if (site.pricePurchase.calculationType === "residual") {
        const { documentFacade, priceManager, flowFacade } =
          createPriceComputationSandbox(
            this._appraisalDocument.$model,
            this._salesTaxConfiguration?.$model ?? null,
          );

        return getPricePurchaseGuessBySite(
          documentFacade,
          priceManager,
          flowFacade,
          this._appraisalDocument.sitePurchases.indexOf(site),
        );
      } else {
        throw new UnreachableCaseError(site.pricePurchase.calculationType);
      }
    },
  );
}

/**
 * A decorator for SitePriceManager that allows overriding the computed price
 * for a specific site purchase.
 *
 * This class wraps an existing SitePriceManager instance. When a price override
 * is set for a site, that value will be returned in preference to the wrapped
 * manager's calculation. This is necessary when using a root finding algorithm
 * like when we look for the residual value. We can fix the value for a given
 * iteration, and it will affect the whole model.
 */
export class OverridableSitePriceManager implements SitePriceManager {
  _sitePriceManager;

  _priceOverride: {
    site: SitePurchaseFacade;
    value: BigFraction;
  } | null = null;

  constructor(sitePriceManager: SitePriceManager) {
    makeAutoObservable(this);

    this._sitePriceManager = sitePriceManager;
  }

  getPriceBySite(site: SitePurchaseFacade) {
    if (this._priceOverride != null && this._priceOverride.site === site) {
      return this._priceOverride.value;
    }
    return this._sitePriceManager.getPriceBySite(site);
  }

  overridePriceBySite = action(
    // Explicitly marje this method as an action otherwise Mobx consider it a
    // derivation (cf. https://mobx.js.org/observable-state.html#autoAction).
    (site: SitePurchaseFacade, value: BigFraction) => {
      this._priceOverride = { site, value };
    },
  );
}

export const getPricePurchaseGuessBySite = (
  documentFacade: AppraisalDocumentFacade,
  priceManager: OverridableSitePriceManager,
  flowFacade: FlowFacade,
  sitePurchaseIndex: number,
  // eslint-disable-next-line max-params
) => {
  // We can't directly use the passed `SitePurchaseFacade` instance since
  // it won't in the sandboxed tree. So we find the index in the original tree
  // and get the matching facade in the sandbox.
  const sitePurchaseClone = expectAtIndex(
    documentFacade.sitePurchases,
    sitePurchaseIndex,
  );

  // Ideally, the lower bound should also factor the target profit in. But since
  // it is composed with the price purchase, it would create a Mobx infinite
  // reaction, so we instead us X times the build costs as heuristic.
  const lowerBound =
    documentFacade
      .getBuildCostTotalWithContingency("any")
      ?.mul(5)
      ?.neg()
      ?.valueOf() ?? 0;

  const upperBound =
    documentFacade.stages.revenue.getGrossDevelopmentValue("any")?.valueOf() ??
    0;

  return bigFractionFromNumber(
    brentZero(
      (priceGuess) => {
        priceManager.overridePriceBySite(
          sitePurchaseClone,
          bigFractionFromNumber(priceGuess),
        );

        return (
          sum([
            flowFacade.developmentProfit,
            opposite(flowFacade.targetProfit),
          ])?.valueOf() ?? 0
        );
      },
      lowerBound,
      upperBound,
    ),
  );
};

export const createPriceComputationSandbox = action(
  (
    appraisal: ShapeAppraisalDocument,
    taxConfiguration: ShapeSalesTaxConfiguration | null,
  ) => {
    // We re-wrap the model with new facades to create a sandboxed facades "tree".
    // Thanks to this principle, we can still track changes in the underlying
    // model (to recompute on updates) while isolating the overrides we are going
    // to do in the facades (to avoid infinite reaction loops).
    return createAppraisalFacades({
      document: appraisal,
      taxConfiguration: taxConfiguration,
      priceManagerFactory: overridablePriceManagerFactory,
    });
  },
);
